/*
 * bc-test-chart — v2
 *
 * Interactive force-vs-time curves for recorded break tests. Bespoke rendering,
 * no charting library: the traces go to a <canvas>, everything around them
 * (gridlines, ticks, axis titles, peak markers, crosshair) stays SVG so it keeps
 * inheriting the theme's CSS custom properties.
 *
 * SELF-CONTAINED BY CONTRACT: everything the element needs comes from its own
 * JSON data islands and its own attributes — it never reads anything from the
 * page around it. That is what lets the same element serve the batch page (all
 * of a batch's strength samples in one chart) and a single-test product section.
 *
 * ---------------------------------------------------------------------------
 * MARKUP — one <script> data island PER SERIES
 * ---------------------------------------------------------------------------
 *
 *   <bc-test-chart class="bc-test-chart" data-label="Force–time curves">
 *     <div class="bc-test-chart__plot" data-bc-plot></div>
 *
 *     <script type="application/json" data-bc-test-curve
 *             data-label="Sample 1"
 *             data-series-color="#2a78d6"
 *             data-full-url="https://cdn.shopify.com/…/sample-1.json">
 *       { "units": "kN",
 *         "scan_rate": 500,
 *         "peak":   { "t": 12.34, "value": 31.2 },
 *         "points": [[0, 0.02], [0.002, 0.05], …] }
 *     </script>
 *
 *     <script type="application/json" data-bc-test-curve data-label="Sample 2">…</script>
 *   </bc-test-chart>
 *
 * Element attributes
 *   data-label   optional chart TITLE (caption above the plot).
 *   data-color   optional CSS color for the FIRST series. v1 back-compat — new
 *                markup should use the island's data-series-color instead.
 *   data-trace-mode   "uniform" draws every series in --bc-tc-line at a low
 *                alpha and gives the HIGHLIGHTED one --bc-tc-highlight at full
 *                strength. For hosts where identity comes from a table beside
 *                the plot rather than from colour (the batch report page): a
 *                configuration's samples break within a few hundred newtons of
 *                each other, so five categorical hues say nothing the row
 *                hover does not say better. Omitted → the categorical palette.
 *   data-legend  "off" suppresses the ≥2-series legend, for a host that already
 *                lists the series (again: the batch page's specimen table).
 *                See data-arm: an ARMED chart shows the legend regardless,
 *                because there the chips are the only way to drop a series.
 *   data-arm     "click" makes the chart a PICTURE until it is pressed: it draws
 *                exactly as it always did, but every interaction is inert until
 *                the reader arms it (see ARM TO INTERACT below). Omitted → the
 *                chart is interactive from the moment it builds, which is what
 *                every caller had before the attribute existed.
 *   data-peak-labels  "off" keeps the peak DOTS but drops their always-on value
 *                labels — same reason: the values are in the table.
 *   data-time-bound  "nice" rounds the time domain's upper end up to a whole
 *                tick step, so the longest trace lands INSIDE the plot and the
 *                last tick labels the frame's own edge. Omitted → the domain
 *                ends on the last recorded sample, which is the default and
 *                what every caller had before the attribute existed. The batch
 *                record asks for it because it is a printed page; a single test
 *                on a product page reads fine ending where the capture did.
 *   data-ref-mean / data-ref-mbs  optional horizontal reference rules, in the
 *                chart's own units: the group mean (dashed) and its 3σ MBS
 *                (accent). Both carry a unit-bearing label on the plot.
 *   data-ref-unit  optional unit the two rules above are stated in. A host
 *                derives them from its own table, whose unit is recorded per
 *                row and need not be the unit of the curve JSON: a rule in N
 *                drawn against a kN axis lands three orders of magnitude away
 *                from where it belongs, and a rule folded into the extent
 *                takes the whole plot with it. Declared and mismatched → the
 *                rules are quietly SKIPPED (the figures are already in the
 *                host's table; a wrong rule is worse than no rule, and a
 *                console error helps nobody reading a batch record).
 *                Omitted → the host is asserting they match, which is what
 *                every pre-attribute caller was already doing.
 *
 * Island attributes
 *   data-label         series name (legend + tooltip). Falls back to the element's
 *                      data-label for a lone series, else "Series N".
 *   data-series-color  optional CSS color. Anything the browser can parse,
 *                      including var(--…); resolved through a probe element.
 *                      Omitted → the built-in palette (see PALETTE_STEPS).
 *   data-series-key    optional stable key for the two-way row↔curve trace (see
 *                      "ROW LINKING" below). Defaults to the series index.
 *   data-full-url      optional URL of a full-resolution JSON file with the same
 *                      shape. Fetched lazily on the FIRST zoom/pan interaction,
 *                      never on load, and never retried after a failure.
 *
 * Island JSON
 *   points     required. [t_seconds, value] pairs (objects {t,v} are tolerated).
 *              Re-sorted by t here, so the server may emit them in any order.
 *   units      optional, default "kN". Y-axis unit suffix; first island wins.
 *   peak       optional. Derived from `points` (max value) when absent/partial.
 *              A DECLARED peak is sample-exact and is kept verbatim even after a
 *              full-resolution swap; only a derived one is recomputed.
 *   scan_rate  optional Hz, shown in the caption meta line only; first island wins.
 *
 * The plot host ([data-bc-plot]) is created if it is missing, so bare data
 * islands are a valid element body. Nothing renders without JS — the peak force
 * is always present in the server-rendered strength table, which is the fallback.
 *
 * ---------------------------------------------------------------------------
 * INTERACTION
 * ---------------------------------------------------------------------------
 * (All of it inert until the reader arms the chart, on a host that asked for
 * data-arm="click" — see ARM TO INTERACT below.)
 *
 *   wheel          zoom BOTH axes about the cursor (1× … 100× per axis, each
 *                  clamped to its own data extent) — a zoom into the point
 *                  under the pointer, not a stretch of the time axis alone.
 *                  shift + wheel zooms time only, alt/option + wheel force
 *                  only. At 1× the wheel belongs to the PAGE: zooming in needs
 *                  ctrl/⌘ + wheel (also how a trackpad pinch arrives), and a
 *                  plain wheel just shows a transient "ctrl + scroll to zoom"
 *                  hint — so scrolling past the chart never traps the reader.
 *                  Once zoomed, the chart owns the wheel outright.
 *   drag           pan, in both axes — only once zoomed in, and only along an
 *                  axis that is. Un-zoomed a drag reads the trace instead,
 *                  which is also what keeps one-finger touch scrolling the page
 *                  (touch-action: pan-y until .is-zoomed).
 *   pinch          zoom both axes about the midpoint (touch), measured on the
 *                  true separation of the two fingers.
 *   double-click   reset zoom, both axes (double-tap on touch).
 *   hover / move   crosshair + tooltip at the nearest sample across the visible
 *                  series.
 *   keyboard       +/- zoom both axes about the middle of the plot, 0 reset;
 *                  left/right pan the time window while it is zoomed, up/down
 *                  pan the force window while IT is, and both pairs step the
 *                  sample readout otherwise (shift+arrows always step samples,
 *                  shift halves the step when the time axis is not zoomed).
 *                  Home/End jump to the ends of the visible window. Escape
 *                  clears the readout — and disarms, on an arm-mode chart.
 *                  Enter/Space on an unarmed one arms it.
 *
 * THE TIME WINDOW IS ALWAYS EXPLICIT STATE (`view`, clamped inside `domain`).
 * The FORCE window is state only once the reader has one (`viewY`, clamped
 * inside `domainY`): null means "fit the axis to the data in the time window",
 * which is what every chart shows at rest and what every host had before the y
 * axis could be zoomed at all. Zooming the force axis all the way back out —
 * like any reset path — releases it to null rather than parking it on the full
 * extent, so "zoomed out" and "reset" can never disagree.
 *
 * Every draw buckets each visible series to ~2 points per pixel by min/max
 * bucketing (both extremes of each pixel column, drawn connected), so a spike
 * can never fall between samples and the draw cost is bounded by the plot width
 * rather than the dataset size — 6 series × 20k points stays smooth.
 *
 * ---------------------------------------------------------------------------
 * ARM TO INTERACT — data-arm="click"
 * ---------------------------------------------------------------------------
 * OPT-IN, and off unless the host asks. A record page carries several of these
 * down a long column, and a chart that reads the pointer the whole way down is
 * a tooltip that follows the reader rather than a figure they can look at. So
 * the host may declare the chart a PICTURE first and an instrument second —
 * the same rule (and the same visual language) bc-rig-figure applies to the 3D
 * test-setup figures on that page.
 *
 *   UNARMED   The traces, axes, peak markers and reference rules draw exactly
 *             as they always do — this is the "image". Nothing else is live:
 *             no crosshair or tooltip, no wheel handling at all (the page keeps
 *             its scroll — the handler returns before it could preventDefault),
 *             no drag, pinch, double-click or keyboard zoom, and the plot is
 *             not a tab stop. A full-bleed "Click to interact" badge over the
 *             plot is the affordance and the hit area both.
 *             Row → curve HIGHLIGHTING STAYS LIVE (setHighlight, the linker
 *             below): identifying which trace a table row is, is presentation,
 *             not interaction, and it is how the picture is read.
 *   ARMED     Everything above turns on, plus the legend — forced open even
 *             under data-legend="off", because with the readout live the chips
 *             are the only way to take a series out of the plot.
 *   DISARM    Escape, a click anywhere outside the chart, or the × control.
 *             The chart goes back to being the complete picture: readout
 *             cleared, zoom reset to 1×, EVERY series visible again, legend
 *             hidden, badge back. Repeatable indefinitely.
 *
 * The gate is one flag, `_armed`, read by the pointer/wheel/key handlers. On a
 * chart with no data-arm it is true from the start and never changes, so those
 * handlers behave exactly as they did before this existed.
 *
 * ---------------------------------------------------------------------------
 * ROW LINKING — a table of specimens beside the plot
 * ---------------------------------------------------------------------------
 * The ELEMENT stays self-contained: it exposes setHighlight(key, opts) and fires
 * `bc:test-chart:highlight` (detail.key, null when cleared) whenever the traced
 * series changes, from any cause. A tiny linker at the foot of this file wires
 * that to a host's rows without either side knowing the other's markup:
 *
 *   <div data-bc-chart-link>
 *     <table>…<tr data-bc-series="0" tabindex="0">…</tr>…</table>
 *     <bc-test-chart>… islands with data-series-key="0" …</bc-test-chart>
 *   </div>
 *
 * Hovering or focusing a row traces its curve; hovering a curve marks its row
 * (.is-traced, plus .is-tracing on the container so the rest can recede). All
 * styling is the host's — the linker only moves class names.
 *
 * `opts.immediate` marks a DELIBERATE highlight (a focus, a blur) so its live
 * region announcement is spoken at once; everything else settles for
 * HIGHLIGHT_SAY_MS first, because a pointer crossing a table announces every
 * row it passes otherwise. Highlight changes never rebuild the chart: they take
 * the cheap redraw path (see _redrawHighlight).
 */
(() => {
  if (customElements.get('bc-test-chart')) return;

  const SVGNS = 'http://www.w3.org/2000/svg';
  const MAX_ZOOM = 100;
  const TOOLTIP_MAX_ROWS = 6;
  const DRAG_SLOP = 4; // px before a press becomes a pan
  // How long a highlight has to hold still before it is announced. A pointer
  // sweeping the specimen table or the curves changes the trace on nearly every
  // frame; only the one it comes to rest on is worth a screen reader's breath.
  const HIGHLIGHT_SAY_MS = 300;

  /*
   * Categorical palette. Slot 0 is the theme's own --bc-tc-line (brand primary);
   * slots 1..7 are generated hue steps off that colour's hue, so the whole set
   * rotates with the brand rather than being hardcoded beside it. Each step
   * carries its own saturation and a lightness per surface mode.
   *
   * The offsets, the per-mode lightnesses AND the slot ORDER were picked by the
   * dataviz palette validator (OKLCH lightness band, chroma floor, protan/deutan
   * ΔE and normal-vision ΔE on adjacent pairs, WCAG contrast vs the surface) for
   * base #f0722e on #ffffff and on #1a1a19: adjacent-pair CVD ΔE ≥ 13.9 and
   * normal-vision ΔE ≥ 25.7 in both modes. Do not reorder them casually — the
   * order IS the result. The brand orange itself sits at 2.94:1 on white, just
   * under the 3:1 relief line; the legend swatches and the direct peak labels
   * are the mandated secondary encoding.
   */
  const PALETTE_STEPS = [
    { dh: 168, s: 0.70, ll: 0.41, ld: 0.41 }, // teal
    { dh: 335, s: 0.62, ll: 0.51, ld: 0.56 }, // red
    { dh: 202, s: 0.68, ll: 0.54, ld: 0.59 }, // blue
    { dh: 118, s: 0.55, ll: 0.33, ld: 0.37 }, // green
    { dh: 245, s: 0.65, ll: 0.55, ld: 0.62 }, // violet
    { dh: 52, s: 0.80, ll: 0.27, ld: 0.30 }, // gold
    { dh: 300, s: 0.55, ll: 0.49, ld: 0.54 }, // magenta
  ];

  // ---- small helpers --------------------------------------------------------

  function svgEl(tag, attrs) {
    const node = document.createElementNS(SVGNS, tag);
    if (attrs) {
      for (const k in attrs) {
        if (attrs[k] != null) node.setAttribute(k, attrs[k]);
      }
    }
    return node;
  }

  function htmlEl(tag, className) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  // "Nice" tick step (1 / 2 / 2.5 / 5 / 10 × 10^n) for ~`target` ticks.
  function niceStep(range, target) {
    if (!(range > 0)) return 1;
    const raw = range / Math.max(1, target);
    const pow = Math.pow(10, Math.floor(Math.log10(raw)));
    const n = raw / pow;
    let step;
    if (n <= 1) step = 1;
    else if (n <= 2) step = 2;
    else if (n <= 2.5) step = 2.5;
    else if (n <= 5) step = 5;
    else step = 10;
    return step * pow;
  }

  // Decimals needed to print `step` exactly (so a 2.5 step never labels "3").
  function decimalsForStep(step) {
    if (!(step > 0)) return 0;
    const s = step.toPrecision(12).replace(/0+$/, '');
    const dot = s.indexOf('.');
    if (dot < 0) return 0;
    return Math.min(4, s.length - dot - 1);
  }

  // Decimals for a readout covering `span` — finer traces need more places.
  function decimalsForSpan(span) {
    if (!(span > 0)) return 2;
    if (span >= 20) return 1;
    if (span >= 2) return 2;
    if (span >= 0.2) return 3;
    return 4;
  }

  function fmt(v, dec) {
    return Number(v).toFixed(dec);
  }

  // Same rounding as fmt() but without trailing zeros — for the headline peak,
  // where "31.2 kN" reads better than "31.20 kN". Readouts keep fixed decimals
  // so the tooltip does not jitter in width as the pointer moves.
  function fmtTrim(v, dec) {
    return String(Number(Number(v).toFixed(dec)));
  }

  // ---- colour ---------------------------------------------------------------

  // "rgb(240, 114, 46)" / "rgba(…)" → [r, g, b]. getComputedStyle always hands
  // back one of those two forms for a resolved <color>, so nothing else is parsed.
  function parseRgb(str) {
    const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/.exec(str || '');
    if (!m) return null;
    return [Number(m[1]), Number(m[2]), Number(m[3])];
  }

  function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    const d = max - min;
    if (d) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
    }
    return [h, s, l];
  }

  // Perceived brightness of the surface, to choose the light/dark lightness set.
  function relLuma(rgb) {
    const f = (c) => {
      const v = c / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * f(rgb[0]) + 0.7152 * f(rgb[1]) + 0.0722 * f(rgb[2]);
  }

  // Comma syntax on purpose: the space-separated form is not understood by
  // every canvas implementation this theme still sees.
  function hsl(h, s, l) {
    return 'hsl(' + (((h % 360) + 360) % 360).toFixed(1) + ', ' +
      (s * 100).toFixed(1) + '%, ' + (l * 100).toFixed(1) + '%)';
  }

  // ---- data -----------------------------------------------------------------

  // Tolerates [t, v] pairs and {t, v} objects; drops junk; sorts by t.
  function readPoints(raw) {
    const list = Array.isArray(raw) ? raw : [];
    const pts = [];
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      let t;
      let v;
      if (Array.isArray(p)) {
        t = Number(p[0]);
        v = Number(p[1]);
      } else if (p && typeof p === 'object') {
        t = Number(p.t != null ? p.t : p.time);
        v = Number(p.v != null ? p.v : p.value != null ? p.value : p.force);
      } else {
        continue;
      }
      if (isFinite(t) && isFinite(v)) pts.push([t, v]);
    }
    pts.sort((a, b) => a[0] - b[0]);
    return pts;
  }

  function derivePeak(pts) {
    let best = pts[0];
    for (let i = 1; i < pts.length; i++) {
      if (pts[i][1] > best[1]) best = pts[i];
    }
    return { t: best[0], value: best[1] };
  }

  // First index with points[i][0] >= t.
  function lowerBound(pts, t) {
    let lo = 0;
    let hi = pts.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (pts[mid][0] < t) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  function nearestIndex(pts, t) {
    const i = lowerBound(pts, t);
    if (i <= 0) return 0;
    if (i >= pts.length) return pts.length - 1;
    return t - pts[i - 1][0] <= pts[i][0] - t ? i - 1 : i;
  }

  /*
   * Min/max bucketing. One bucket per plot pixel; each non-empty bucket emits
   * BOTH its extremes, in time order, so the vertical span of that column is
   * drawn and a one-sample spike survives any zoom level. Returns a flat
   * [t, v, t, v, …] list plus the value range of the samples actually inside
   * the window (the two straddling samples are drawn — they are what makes the
   * trace reach the plot edges — but they never stretch the y-axis).
   */
  function bucketize(pts, t0, t1, nBuckets) {
    const n = pts.length;
    const span = t1 - t0 || 1;
    let lo = lowerBound(pts, t0) - 1;
    if (lo < 0) lo = 0;
    let hi = lowerBound(pts, t1) + 1;
    if (hi > n) hi = n;

    const flat = [];
    let vMin = Infinity;
    let vMax = -Infinity;

    let bi = null;
    let minV = 0;
    let maxV = 0;
    let minT = 0;
    let maxT = 0;

    const flush = () => {
      if (bi === null) return;
      if (minT === maxT) {
        flat.push(minT, minV);
      } else if (minT < maxT) {
        flat.push(minT, minV, maxT, maxV);
      } else {
        flat.push(maxT, maxV, minT, minV);
      }
      bi = null;
    };

    for (let i = lo; i < hi; i++) {
      const t = pts[i][0];
      const v = pts[i][1];
      let b = Math.floor(((t - t0) / span) * nBuckets);
      if (b < 0) b = -1;
      else if (b >= nBuckets) b = nBuckets;
      else {
        if (v < vMin) vMin = v;
        if (v > vMax) vMax = v;
      }
      if (b !== bi) {
        flush();
        bi = b;
        minV = maxV = v;
        minT = maxT = t;
      } else {
        if (v < minV) {
          minV = v;
          minT = t;
        }
        if (v > maxV) {
          maxV = v;
          maxT = t;
        }
      }
    }
    flush();

    return { flat, vMin, vMax, empty: vMin === Infinity };
  }

  // ---- element --------------------------------------------------------------

  class BcTestChart extends HTMLElement {
    connectedCallback() {
      // Re-insertion runs this again — a section morph re-parenting the element,
      // a details/tab panel detached and put back. The one-time island read below
      // must not repeat, but disconnectedCallback tore the observers down, so a
      // reconnect that did nothing else would leave the element alive and deaf:
      // no resize reflow, no theme redraw. Re-establish them instead (both calls
      // are idempotent and no-op on an element with nothing to draw), and treat
      // the detached interval as a possible theme/size change.
      if (this._init) {
        if (this._built) {
          this._observe();
          this._colorsDirty = true;
          this._scheduleRender();
        } else {
          this._observeIntersection();
        }
        return;
      }
      this._init = true;

      const islands = this.querySelectorAll('script[type="application/json"][data-bc-test-curve]');
      if (!islands.length) return;

      // NB: never `this.title` — that is HTMLElement's tooltip attribute.
      this.chartTitle = (this.dataset.label || '').trim();
      const elColor = (this.dataset.color || '').trim(); // v1 back-compat

      // Presentation switches (see the header comment). Each is opt-OUT/opt-IN
      // by an explicit token, never by presence, so an empty attribute left by a
      // Liquid conditional behaves exactly like an absent one.
      this.traceMode = (this.dataset.traceMode || '').trim();
      this.showLegend = (this.dataset.legend || '').trim() !== 'off';
      this.peakLabels = (this.dataset.peakLabels || '').trim() !== 'off';
      this.niceTimeBound = (this.dataset.timeBound || '').trim() === 'nice';

      // Arm to interact (see the header comment). `_armed` is THE gate every
      // pointer/wheel/key handler reads, and on a chart that never asked for
      // arming it is true here and stays true forever — which is what keeps
      // every pre-attribute host behaving exactly as it did.
      this.armMode = (this.dataset.arm || '').trim() === 'click';
      this._armed = !this.armMode;

      // Horizontal reference rules. Read as STRINGS first: Number('') is 0 and
      // would silently plant a rule on the baseline of every chart that omits
      // the attribute.
      this.refs = [];
      const refDefs = [
        { raw: this.dataset.refMean, kind: 'mean', label: 'Mean' },
        { raw: this.dataset.refMbs, kind: 'mbs', label: '3σ MBS' },
      ];
      for (const def of refDefs) {
        const raw = (def.raw || '').trim();
        if (!raw) continue;
        const v = Number(raw);
        if (!isFinite(v)) continue;
        this.refs.push({ value: v, kind: def.kind, label: def.label });
      }
      // Held for the unit check below: `this.units` is not known until the
      // islands have been read, and a rule stated in a unit the axis does not
      // use must never be drawn (see data-ref-unit in the header comment).
      const refUnit = (this.dataset.refUnit || '').trim();

      this.series = [];
      let units = '';
      let scanRate = null;

      for (let i = 0; i < islands.length; i++) {
        const node = islands[i];
        let data;
        try {
          data = JSON.parse(node.textContent);
        } catch (e) {
          continue;
        }
        if (!data || typeof data !== 'object') continue;

        const pts = readPoints(data.points);
        if (pts.length < 2) continue;

        let peak = null;
        let peakDeclared = false;
        if (data.peak && isFinite(Number(data.peak.value))) {
          const pt = Number(data.peak.t);
          peak = { t: isFinite(pt) ? pt : null, value: Number(data.peak.value) };
          peakDeclared = true;
        }
        if (!peak || peak.t == null) {
          const d = derivePeak(pts);
          peak = peak ? { t: d.t, value: peak.value } : d;
        }

        if (!units && typeof data.units === 'string' && data.units.trim()) units = data.units.trim();
        const sr = Number(data.scan_rate);
        if (scanRate == null && isFinite(sr) && sr > 0) scanRate = sr;

        this.series.push({
          index: this.series.length,
          key: (node.dataset.seriesKey || '').trim() || String(this.series.length),
          label: (node.dataset.label || '').trim(),
          colorAttr: (node.dataset.seriesColor || '').trim() ||
            (this.series.length === 0 ? elColor : ''),
          fullUrl: (node.dataset.fullUrl || '').trim(),
          points: pts,
          peak: peak,
          peakDeclared: peakDeclared,
          visible: true,
          loading: false,
          loaded: false,
          failed: false,
          color: '',
        });
      }
      if (!this.series.length) return;

      // The caption names the chart. With no element title and a single series,
      // that series' own name is the honest caption — which is what keeps a
      // one-curve host from having to special-case anything.
      this.multi = this.series.length > 1;
      this.capLabel = this.chartTitle || (this.multi ? '' : this.series[0].label);
      if (!this.multi && !this.series[0].label) this.series[0].label = this.chartTitle;
      this.series.forEach((s, i) => {
        if (!s.label) s.label = 'Series ' + (i + 1);
      });

      this.units = units || 'kN';
      this.scanRate = scanRate;

      // The reference rules are the host's figures, and the host derives them
      // from its own table — whose unit is recorded per row and is not
      // guaranteed to be the unit of the curve JSON. Compared case- and
      // space-insensitively (a unit is a symbol, not prose); a mismatch drops
      // BOTH rules silently, since a rule drawn at the wrong height is a false
      // statement about the data and the values are already in the host's
      // table. No declared unit means the host is asserting the two agree.
      if (refUnit && this.refs.length &&
          refUnit.replace(/\s+/g, '').toLowerCase() !==
            String(this.units).replace(/\s+/g, '').toLowerCase()) {
        this.refs = [];
      }

      // The FORCE window. null is "fit the visible data", which is what every
      // chart shows at rest and what every caller had before the y axis could
      // be zoomed at all; a non-null value is a window the reader zoomed or
      // panned into, clamped to the full-data extent (see _clampView).
      this.viewY = null;
      this._recomputeDomain(true);
      this.activeT = null;
      this.kbIndex = null;
      this.highlight = null;
      this._hiSource = null;
      this._pending = 0;
      this._fullRequested = false;
      this._colorsDirty = true;

      // Plot host — created when the markup omits it, so bare islands work.
      this.plot = this.querySelector('[data-bc-plot]');
      if (!this.plot) {
        this.plot = htmlEl('div', 'bc-test-chart__plot');
        this.plot.setAttribute('data-bc-plot', '');
        this.insertBefore(this.plot, islands[0]);
      }

      // Colour probe: an empty inline element whose `color` we set to the raw
      // token and read back resolved. Handles var(), colour names and hexes
      // alike, and tells us whether an authored value parsed at all.
      this._probe = htmlEl('span', 'bc-test-chart__probe');
      this._probe.setAttribute('aria-hidden', 'true');
      this.insertBefore(this._probe, this.plot);

      this._buildCaption();

      this._build = this._build.bind(this);
      this._observeIntersection();
    }

    disconnectedCallback() {
      if (this._io) {
        this._io.disconnect();
        this._io = null; // so a reconnect can re-arm the lazy build
      }
      if (this._ro) {
        this._ro.disconnect();
        this._roBound = false;
      }
      if (this._onResize && this._resizeBound) {
        window.removeEventListener('resize', this._onResize);
        this._resizeBound = false;
      }
      if (this._mq && this._onScheme && this._schemeBound) {
        if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._onScheme);
        else if (this._mq.removeListener) this._mq.removeListener(this._onScheme);
        this._schemeBound = false;
      }
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = null;
      this._rafFull = false;
      clearTimeout(this._zoomHintT);
      clearTimeout(this._liveT);
      this._liveT = null;
    }

    // ---- observers ----------------------------------------------------------

    // Lazy build. Guarded on having something to build: an element whose islands
    // were all unusable returns from connectedCallback before series exist, and
    // must never reach _build — on the first connect it never gets here, on a
    // reconnect it calls straight in.
    _observeIntersection() {
      if (this._io || this._built) return;
      if (!this.series || !this.series.length || !this.plot) return;
      if (!('IntersectionObserver' in window)) {
        this._build();
        return;
      }
      this._io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this._io.disconnect();
          this._io = null;
          this._build();
        }
      }, { rootMargin: '300px 0px' });
      this._io.observe(this);
    }

    // Resize + colour-scheme watchers. Called from _build and again from every
    // reconnect, so each branch is guarded by its own bound flag: re-observing a
    // live ResizeObserver or double-adding a legacy matchMedia listener would
    // otherwise cost a duplicate render per event.
    _observe() {
      if (!this._built) return;

      if ('ResizeObserver' in window) {
        if (!this._ro) {
          this._ro = new ResizeObserver(() => {
            this._colorsDirty = true;
            this._scheduleRender();
          });
        }
        if (!this._roBound) {
          this._ro.observe(this.plot);
          this._roBound = true;
        }
      } else {
        if (!this._onResize) {
          this._onResize = () => {
            this._colorsDirty = true;
            this._scheduleRender();
          };
        }
        if (!this._resizeBound) {
          window.addEventListener('resize', this._onResize);
          this._resizeBound = true;
        }
      }

      // Canvas colours are baked at draw time, so a theme flip needs a redraw
      // (the SVG half re-themes itself through CSS with no help).
      if (window.matchMedia) {
        if (!this._mq) this._mq = window.matchMedia('(prefers-color-scheme: dark)');
        if (!this._onScheme) {
          this._onScheme = () => {
            this._colorsDirty = true;
            this._scheduleRender();
          };
        }
        if (!this._schemeBound) {
          if (this._mq.addEventListener) this._mq.addEventListener('change', this._onScheme);
          else if (this._mq.addListener) this._mq.addListener(this._onScheme);
          this._schemeBound = true;
        }
      }
    }

    // ---- domain / view ------------------------------------------------------

    // Union of every series' recorded time span. Called again after a
    // full-resolution swap, which can widen a series slightly.
    _recomputeDomain(initial) {
      let t0 = Infinity;
      let t1 = -Infinity;
      for (const s of this.series) {
        const p = s.points;
        if (p[0][0] < t0) t0 = p[0][0];
        if (p[p.length - 1][0] > t1) t1 = p[p.length - 1][0];
      }
      if (!isFinite(t0) || !isFinite(t1) || t1 <= t0) t1 = t0 + 1;
      // A NICE UPPER BOUND ON THE TIME AXIS, the y axis's opposite number, for a
      // host that asked for one (data-time-bound="nice"). Left un-rounded — the
      // default, and what every caller had before this — the domain ends on the
      // last recorded sample, so the longest trace terminates exactly ON the
      // right frame edge and the final tick lands well short of it: a 165 s pull
      // labelled to 150 s with the break drawn into the frame. Rounding the SPAN
      // up to a whole tick step puts the last trace inside the plot and the last
      // tick on the edge.
      //
      // OPT-IN rather than the default, deliberately. A time axis that stops at
      // the last sample is an honest and common choice — it says the record ends
      // there — and the batch record wants the other one because it is a printed
      // page whose frame has to be labelled to its own edge. Nothing changes for
      // a caller that does not ask.
      //
      // t0 is left alone either way: these are pulls from zero, and moving the
      // origin would shift every trace off the axis it starts on.
      //
      // The step is taken at the wide tick target; a narrow plot re-derives a
      // coarser one at render, which can only land the bound between ticks, not
      // back on the trace. Idempotent, so the full-resolution swap re-running
      // this cannot creep the bound outward one step at a time.
      if (this.niceTimeBound) {
        const tPad = niceStep(t1 - t0, 6);
        if (tPad > 0) t1 = t0 + Math.ceil((t1 - t0) / tPad - 1e-9) * tPad;
      }
      const wasFull = !initial && this.view &&
        this.view.t0 <= this.domain.t0 + 1e-9 && this.view.t1 >= this.domain.t1 - 1e-9;
      this.domain = { t0: t0, t1: t1 };
      if (initial || !this.view || wasFull) this.view = { t0: t0, t1: t1 };
      this._recomputeYDomain();
      this._clampView();
    }

    /*
     * NICE AXIS BOUNDS FOR A FORCE RANGE — the y axis's whole rounding policy,
     * in one place because two callers need the identical answer: the render's
     * fit-to-the-visible-data path, and the full-data y DOMAIN below (which has
     * to come out equal to the fit at full zoom, or resetting the zoom would
     * land somewhere the pan clamp had never allowed).
     *
     * Force traces sit on a zero baseline and stay on it at every zoom level — a
     * floating baseline would make a 2 kN wobble look like a break. Only a
     * material negative excursion extends the axis downward, so a sensor's
     * sub-1% negative noise never buys itself a whole tick band…
     */
    _niceYBounds(vMin, vMax) {
      const lowRaw = vMin < -Math.abs(vMax) * 0.02 ? vMin : 0;
      // …and the ROUNDING of that low bound is the second gate, because the
      // relative test alone is not enough. A load cell reads a few hundred
      // newtons negative as the specimen rebounds off a break — 0.8 kN on a
      // 25 kN pull, comfortably past the 2% test — and flooring THAT to a whole
      // tick step drops the axis to -10 kN, spending a quarter of the plot on a
      // region no force ever occupied. So a negative excursion buys a tick band
      // only once it is worth half of one. Anything smaller keeps the zero
      // baseline and the rebound tail simply runs off it, which is exactly
      // right for a force trace and costs nothing: _drawTraces clips to the
      // plot rect. Genuinely negative data — a compression trace, a sensor
      // pulled below zero and staying there — still extends the axis downward.
      //
      // The step is derived twice on purpose: once to size the half-band test,
      // then again from the range that survives it, so the ticks are nice for
      // the axis actually drawn. It cannot oscillate — the second pass is never
      // fed the bound the first one rejected.
      const stepProbe = niceStep(Math.max(vMax - lowRaw, Math.abs(vMax) || 1), 5);
      const lowKeep = lowRaw > -stepProbe * 0.5 ? 0 : lowRaw;
      const step = niceStep(Math.max(vMax - lowKeep, Math.abs(vMax) || 1), 5);
      return {
        lo: Math.floor(lowKeep / step) * step,
        // Headroom for the peak label.
        hi: Math.ceil((vMax + step * 0.35) / step) * step,
        step: step,
      };
    }

    /*
     * The FULL-DATA force extent — the y axis's opposite number to `domain`,
     * and what the y window is clamped inside so a pan can never wander off
     * into empty space. Taken over every series' whole point list (not just the
     * visible ones, and not just the visible time window) so that hiding a
     * legend chip or panning the time axis cannot move the wall the reader is
     * panning against. Recomputed with the time domain, so a full-resolution
     * swap that widens a trace widens this too.
     */
    _recomputeYDomain() {
      let vMin = Infinity;
      let vMax = -Infinity;
      for (const s of this.series) {
        const p = s.points;
        for (let i = 0; i < p.length; i++) {
          const v = p[i][1];
          if (v < vMin) vMin = v;
          if (v > vMax) vMax = v;
        }
        // A declared peak is sample-exact and outranks the trace's resolution.
        if (s.peakDeclared && s.peak.value > vMax) vMax = s.peak.value;
      }
      if (vMin === Infinity) {
        vMin = 0;
        vMax = 1;
      }
      // Reference rules belong inside the extent for the same reason they are
      // folded into the render's fit: a rule the reader cannot pan to is a rule
      // that silently vanished. The same "not absurdly far off the data" guard
      // applies, though at FULL domain the visible peak is the data peak and a
      // mean or a 3σ MBS is at or below it by construction, so this is only
      // ever insurance.
      for (const r of this.refs) {
        if (vMax > 0 && r.value > vMax * 3) continue;
        if (r.value < vMin) vMin = r.value;
        if (r.value > vMax) vMax = r.value;
      }
      const b = this._niceYBounds(vMin, vMax);
      this.domainY = { v0: b.lo, v1: b.hi };
    }

    _domainSpan() {
      return this.domain.t1 - this.domain.t0 || 1;
    }

    _domainSpanY() {
      return this.domainY ? this.domainY.v1 - this.domainY.v0 || 1 : 1;
    }

    _zoom() {
      return this._domainSpan() / (this.view.t1 - this.view.t0 || 1);
    }

    _zoomY() {
      if (!this.viewY) return 1;
      return this._domainSpanY() / (this.viewY.v1 - this.viewY.v0 || 1);
    }

    // Either axis being zoomed is what turns the chart from a reader into an
    // instrument: it owns the drag (and, through .is-zoomed, the touch gesture)
    // and it offers the reset control.
    _isZoomed() {
      return this._zoom() > 1.001 || this._zoomY() > 1.001;
    }

    // The force window ACTUALLY on screen: the reader's, when they have one,
    // else whatever the last render fitted to the data (and, before the first
    // render, the full extent). Every y zoom and pan starts from this, so
    // "zoom in on what I am looking at" holds whether or not a window is held.
    _effectiveY() {
      if (this.viewY) return this.viewY;
      if (this._yLo != null && this._yHi != null) return { v0: this._yLo, v1: this._yHi };
      return this.domainY ? { v0: this.domainY.v0, v1: this.domainY.v1 } : { v0: 0, v1: 1 };
    }

    // Identity of the y window, for the "did anything move" test. 'auto' is a
    // state of its own — releasing a window back to the fit IS a change.
    _viewYKey() {
      return this.viewY ? this.viewY.v0 + '/' + this.viewY.v1 : 'auto';
    }

    // Keeps both windows inside the data span and within 1× … MAX_ZOOM.
    _clampView() {
      const dSpan = this._domainSpan();
      let span = this.view.t1 - this.view.t0;
      span = clamp(span, dSpan / MAX_ZOOM, dSpan);
      let t0 = clamp(this.view.t0, this.domain.t0, this.domain.t1 - span);
      this.view = { t0: t0, t1: t0 + span };

      if (this.viewY && this.domainY) {
        const dSpanY = this._domainSpanY();
        const spanY = clamp(this.viewY.v1 - this.viewY.v0, dSpanY / MAX_ZOOM, dSpanY);
        // A window that has been zoomed all the way back out is not a window —
        // it is the fit again. Releasing it here (rather than parking it on the
        // full extent) is what keeps zooming out and resetting land in exactly
        // the same place, and hands the axis back to the data as soon as the
        // reader stops holding it.
        //
        // The threshold is _isZoomed's, deliberately: anything it would call
        // un-zoomed must actually BE un-zoomed, or a hair of residual window
        // would survive with the reset control hidden and no way back. (The fit
        // and the full extent are not bit-identical — the fit is taken over the
        // buckets of the visible series, the extent over every raw sample — so
        // an exact-equality release would leave exactly that gap.)
        if (spanY >= dSpanY / 1.001) {
          this.viewY = null;
        } else {
          const v0 = clamp(this.viewY.v0, this.domainY.v0, this.domainY.v1 - spanY);
          this.viewY = { v0: v0, v1: v0 + spanY };
        }
      }

      const zoomed = this._isZoomed();
      this.classList.toggle('is-zoomed', zoomed);
      if (this._resetBtn) this._resetBtn.hidden = !zoomed;
    }

    /*
     * Zoom by `factor` keeping the point under (anchorT, anchorV) pinned.
     *
     * `axes` selects what moves — 'both' (the default, and what a plain wheel,
     * a pinch and the +/- keys do), 'x' or 'y'. Both axes scale by the SAME
     * factor about the same anchor, so a wheel zoom is a true 2D zoom into the
     * cursor rather than two independent scales that shear the curve's shape.
     *
     * Returns true if either window actually moved, so callers know whether to
     * swallow the event.
     */
    _zoomBy(factor, anchorT, anchorV, axes) {
      const mode = axes || 'both';
      const beforeX = this.view.t0 + '/' + this.view.t1;
      const beforeY = this._viewYKey();

      if (mode !== 'y') {
        const span = this.view.t1 - this.view.t0;
        const a = clamp(anchorT, this.view.t0, this.view.t1);
        const frac = (a - this.view.t0) / (span || 1);
        const next = span / factor;
        this.view = { t0: a - frac * next, t1: a + (1 - frac) * next };
      }
      if (mode !== 'x' && this.domainY) {
        const cur = this._effectiveY();
        const span = cur.v1 - cur.v0;
        const av = anchorV == null ? (cur.v0 + cur.v1) / 2 : anchorV;
        const a = clamp(av, cur.v0, cur.v1);
        const frac = (a - cur.v0) / (span || 1);
        const next = span / factor;
        this.viewY = { v0: a - frac * next, v1: a + (1 - frac) * next };
      }

      this._clampView();
      const moved = beforeX !== this.view.t0 + '/' + this.view.t1 || beforeY !== this._viewYKey();
      if (moved) {
        this._onInteract();
        this._scheduleRender();
      }
      return moved;
    }

    _panBy(dt) {
      const span = this.view.t1 - this.view.t0;
      const t0 = clamp(this.view.t0 + dt, this.domain.t0, this.domain.t1 - span);
      if (Math.abs(t0 - this.view.t0) < 1e-12) return false;
      this.view = { t0: t0, t1: t0 + span };
      this._onInteract();
      this._scheduleRender();
      return true;
    }

    // Force-axis pan. Only meaningful while a y window is held: with the axis
    // fitted to the data there is nothing above or below to pan to.
    _panByV(dv) {
      if (!this.viewY || !this.domainY) return false;
      const span = this.viewY.v1 - this.viewY.v0;
      const v0 = clamp(this.viewY.v0 + dv, this.domainY.v0, this.domainY.v1 - span);
      if (Math.abs(v0 - this.viewY.v0) < 1e-12) return false;
      this.viewY = { v0: v0, v1: v0 + span };
      this._onInteract();
      this._scheduleRender();
      return true;
    }

    // Both axes, always: the reset control, Escape, the × and a double-click
    // all mean "give me the whole picture back", and a chart that returned the
    // time axis while still holding a force window would be a figure that lies
    // about the batch.
    _resetZoom() {
      if (!this._isZoomed()) return;
      this.view = { t0: this.domain.t0, t1: this.domain.t1 };
      this.viewY = null;
      this._clampView();
      this._scheduleRender();
    }

    // ---- lazy full resolution ----------------------------------------------

    // The FIRST zoom or pan is the signal that the preview's ~800 points are no
    // longer enough. Load-time fetches are deliberately avoided: most visitors
    // never zoom.
    _onInteract() {
      if (this._fullRequested) return;
      this._fullRequested = true;
      this.series.forEach((s) => this._loadFull(s));
    }

    _loadFull(s) {
      if (!s.fullUrl || !s.visible || s.loading || s.loaded || s.failed) return;
      if (typeof fetch !== 'function') {
        s.failed = true;
        return;
      }
      s.loading = true;
      this._pending += 1;
      this._syncHint();
      const done = () => {
        s.loading = false;
        this._pending -= 1;
        this._syncHint();
        this._recomputeDomain(false);
        this._scheduleRender();
      };
      // A hung connection must not leave the "Loading full resolution…" hint up
      // forever — abort into the same silent-fallback path as a failed fetch.
      const opts = { credentials: 'omit' };
      if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) {
        opts.signal = AbortSignal.timeout(15000);
      }
      fetch(s.fullUrl, opts)
        .then((r) => {
          if (!r.ok) throw new Error('http ' + r.status);
          return r.json();
        })
        .then((json) => {
          const pts = readPoints(json && json.points);
          if (pts.length < 2) throw new Error('no points');
          s.points = pts;
          s.loaded = true;
          // An in-progress keyboard walk indexes the OLD points array; drop it
          // so the arrows resume from wherever the reader is, not sample #n of
          // a differently sized series.
          if (this.kbIndex != null && s === this._primary()) this.kbIndex = null;
          // A declared peak is sample-exact from the test rig and outranks any
          // resolution of the trace; only a derived one is refreshed.
          if (!s.peakDeclared) s.peak = derivePeak(pts);
        })
        .catch(() => {
          // Keep the preview silently and never retry: a failed CDN fetch must
          // not cost the reader their chart.
          s.failed = true;
        })
        .then(done, done);
    }

    _syncHint() {
      if (!this._hint) return;
      this._hint.classList.toggle('is-visible', this._pending > 0);
    }

    // ---- caption / legend ---------------------------------------------------

    _captionMeta() {
      const bits = [];
      if (this.multi) bits.push(this.series.length + ' samples');
      else bits.push('Peak ' + fmtTrim(this.series[0].peak.value, 2) + ' ' + this.units);
      if (this.scanRate) bits.push(this.scanRate + ' Hz');
      return bits.join(' · ');
    }

    _buildCaption() {
      const cap = htmlEl('div', 'bc-test-chart__caption');
      if (this.capLabel) {
        const name = htmlEl('span', 'bc-test-chart__caption-label');
        name.textContent = this.capLabel;
        cap.appendChild(name);
      }
      const meta = htmlEl('span', 'bc-test-chart__caption-meta');
      meta.textContent = this._captionMeta();
      cap.appendChild(meta);

      // Zoom affordance: hidden until the reader has actually zoomed, so the
      // resting chart stays as quiet as v1's.
      const reset = htmlEl('button', 'bc-test-chart__reset');
      reset.type = 'button';
      reset.textContent = 'Reset zoom';
      reset.hidden = true;
      reset.addEventListener('click', () => {
        this._resetZoom();
        if (this.svg) this.svg.focus();
      });
      this._resetBtn = reset;
      cap.appendChild(reset);

      this.insertBefore(cap, this.plot);
    }

    // Legend: identity is never colour-alone — every series carries a name, and
    // the peak value rides the chip so the headline numbers stay readable when
    // the plot gets busy. Only for ≥ 2 series; a lone series is named by the
    // caption.
    // An ARM-MODE chart builds the legend even under data-legend="off" and
    // keeps it hidden until it is armed (_syncLegend): a host suppresses the
    // legend because its own table already names the series, but once the
    // reader is holding the chart the chips are the only way to take a series
    // OUT of the plot, and there is no table column for that.
    _buildLegend() {
      if (!this.multi || (!this.showLegend && !this.armMode)) return;
      const legend = htmlEl('div', 'bc-test-chart__legend');
      legend.setAttribute('role', 'group');
      legend.setAttribute('aria-label', 'Toggle series');

      this.series.forEach((s) => {
        const chip = htmlEl('button', 'bc-test-chart__chip');
        chip.type = 'button';
        chip.setAttribute('aria-pressed', 'true');

        const sw = htmlEl('span', 'bc-test-chart__chip-swatch');
        const name = htmlEl('span', 'bc-test-chart__chip-name');
        name.textContent = s.label;
        const val = htmlEl('span', 'bc-test-chart__chip-val');
        val.textContent = fmtTrim(s.peak.value, 2) + ' ' + this.units;
        chip.appendChild(sw);
        chip.appendChild(name);
        chip.appendChild(val);

        chip.addEventListener('click', () => {
          // At least one series always stays plotted.
          if (s.visible && this.series.filter((o) => o.visible).length <= 1) return;
          s.visible = !s.visible;
          chip.setAttribute('aria-pressed', String(s.visible));
          if (s.visible && this._fullRequested) this._loadFull(s);
          this._clearActive();
          // _clearActive drops the highlight with the readout — but the pointer
          // is still ON this chip, so its hover has to be re-asserted or the
          // trace vanishes under a stationary cursor. A series just hidden has
          // nothing left to trace.
          this._setHighlight(s.visible ? s : null);
          this._scheduleRender();
        });

        // Focus and blur are deliberate — the reader asked for this chip — so
        // they announce at once. A pointer only passing over settles first.
        const on = (immediate) => this._setHighlight(s, immediate);
        const off = (immediate) => {
          if (this.highlight !== s) return;
          this._setHighlight(null, immediate);
        };
        chip.addEventListener('pointerenter', () => on(false));
        chip.addEventListener('pointerleave', () => off(false));
        chip.addEventListener('focus', () => on(true));
        chip.addEventListener('blur', () => off(true));

        s.chip = chip;
        s.swatch = sw;
        legend.appendChild(chip);
      });

      this.legendEl = legend;
      this.appendChild(legend);
      this._syncLegend();
    }

    // Shown whenever the host asked for a legend, and additionally whenever an
    // arm-mode chart is armed. `hidden` (not a class) so the chips leave the
    // accessibility tree and the tab order with the affordance — the stylesheet
    // carries the [hidden] rule the legend's own `display: flex` would beat.
    _syncLegend() {
      if (!this.legendEl) return;
      this.legendEl.hidden = !(this._armed || this.showLegend);
    }

    // ---- one-time scaffolding ----------------------------------------------

    _build() {
      if (this._built) return;
      this._built = true;

      // Traces only. Everything legible (ticks, labels, markers) stays SVG so it
      // keeps the theme's CSS custom properties without a redraw.
      this.canvas = htmlEl('canvas', 'bc-test-chart__canvas');
      this.canvas.setAttribute('aria-hidden', 'true');
      this.plot.appendChild(this.canvas);
      this.ctx = this.canvas.getContext('2d');

      const peakTxt = fmtTrim(this.series[0].peak.value, 2) + ' ' + this.units;
      this.svg = svgEl('svg', {
        class: 'bc-test-chart__svg',
        role: 'img',
        // An UNARMED plot drives nothing, so it is not a tab stop — the arm
        // badge is, and it is the one control that does anything from there.
        // _setArmed hands the tab stop back and forth from here on.
        tabindex: this._armed ? '0' : '-1',
        'aria-label':
          (this.capLabel ? this.capLabel + '. ' : '') +
          (this.multi
            ? this.series.length + ' force against time break-test curves'
            : 'Force against time break-test curve') +
          '. Time on the horizontal axis in seconds, force on the vertical axis in ' +
          this.units + '.' +
          (this.multi ? '' : ' Peak ' + peakTxt + '.') +
          ' Press the arrow keys to read the trace, plus and minus to zoom, zero to reset.',
      });
      this.plot.appendChild(this.svg);

      this.tooltip = htmlEl('div', 'bc-test-chart__tooltip');
      this.tooltip.setAttribute('role', 'presentation');
      this.plot.appendChild(this.tooltip);

      this._hint = htmlEl('p', 'bc-test-chart__hint');
      this._hint.setAttribute('aria-hidden', 'true');
      this._hint.textContent = 'Loading full resolution…';
      this.plot.appendChild(this._hint);

      // Shown briefly when a plain wheel lands on the un-zoomed chart, since
      // entering zoom needs the modifier (see _onWheel).
      this._zoomHint = htmlEl('p', 'bc-test-chart__zoom-hint');
      this._zoomHint.setAttribute('aria-hidden', 'true');
      this._zoomHint.textContent =
        (/Mac|iP/.test(navigator.platform || '') ? '⌘' : 'Ctrl') + ' + scroll to zoom';
      this.plot.appendChild(this._zoomHint);

      this.live = htmlEl('div', 'visually-hidden');
      this.live.setAttribute('aria-live', 'polite');
      this.plot.appendChild(this.live);

      this._buildLegend();
      if (this.armMode) this._buildArmUi();
      this._wireEvents();
      this._observe();

      this._render();
    }

    // ---- arm to interact ----------------------------------------------------

    // Two controls, in the same idiom as the 3D test-setup figures beside these
    // charts (bc-rig-figure): a full-bleed press-to-arm layer wearing a corner
    // badge, and a × that gives the picture back. Only built for data-arm
    // hosts, so nothing else gains a node.
    _buildArmUi() {
      // FULL-BLEED, unlike the rig figure's badge-only hit area: arming costs
      // nothing here (no library, no megabyte), so "click the picture" is the
      // whole gesture and the badge is just where it says so.
      const arm = htmlEl('button', 'bc-test-chart__arm');
      arm.type = 'button';
      arm.setAttribute('aria-pressed', 'false');
      arm.setAttribute(
        'aria-label',
        'Interact with this chart: read values along the curves, zoom, and show or hide individual tests'
      );
      const badge = htmlEl('span', 'bc-test-chart__arm-badge');
      badge.textContent = 'Click to interact';
      arm.appendChild(badge);
      arm.addEventListener('click', (e) => {
        e.preventDefault();
        this._arm();
      });
      this._armBtn = arm;
      this.plot.appendChild(arm);

      // Escape and a click outside both disarm, but neither is discoverable.
      const off = htmlEl('button', 'bc-test-chart__disarm');
      off.type = 'button';
      off.setAttribute('aria-label', 'Stop interacting with this chart (Escape)');
      const x = htmlEl('span', 'bc-test-chart__disarm-x');
      x.setAttribute('aria-hidden', 'true');
      x.textContent = '×';
      const done = htmlEl('span', 'bc-test-chart__disarm-t');
      done.textContent = 'Done';
      off.appendChild(x);
      off.appendChild(done);
      off.addEventListener('click', (e) => {
        e.preventDefault();
        this.releaseIfArmed();
      });
      this._disarmBtn = off;
      this.plot.appendChild(off);
    }

    _arm() {
      if (!this.armMode || this._armed) return;
      this._setArmed(true);
      // The badge that was just pressed is `display: none` the instant it arms,
      // so keyboard focus would fall to <body> and drop the reader at the top of
      // the document. The plot is where the arrows, +/- and 0 now go.
      //
      // It is also how the new state is ANNOUNCED, without a second live region
      // competing with the readout's: landing on the plot speaks its aria-label,
      // which already ends in what the keys now do. The way back is the same
      // trade — releaseIfArmed returns focus to the badge, whose own label and
      // aria-pressed="false" say the chart is a picture again.
      if (this.svg && this._armBtn && document.activeElement === this._armBtn) {
        this.svg.focus({ preventScroll: true });
      }
    }

    // Public, for the document-level release below (an Escape or a click that
    // landed somewhere else on the page). A no-op on a chart that never asked
    // for arming, and on one that is already a picture.
    releaseIfArmed() {
      if (!this.armMode || !this._armed) return;
      // Take the focus back only if it was ours: a click three sections down
      // the page must not yank the viewport up to this chart.
      const hadFocus = !!document.activeElement && this.contains(document.activeElement);
      this._setArmed(false);
      if (hadFocus && this._armBtn) this._armBtn.focus({ preventScroll: true });
    }

    // The whole state machine. Disarming must leave the COMPLETE picture behind
    // — the reader's zoom and their hidden series are interaction state, and a
    // chart that goes quiet still holding them is a figure that silently lies
    // about the batch.
    _setArmed(on) {
      if (!this.armMode || !this._built || this._armed === on) return;
      this._armed = on;
      this.classList.toggle('is-armed', on);
      if (this._armBtn) this._armBtn.setAttribute('aria-pressed', String(on));
      if (this.svg) this.svg.setAttribute('tabindex', on ? '0' : '-1');
      this._syncLegend();
      if (on) return;

      this._clearActive();
      if (this._zoomHint) this._zoomHint.classList.remove('is-visible');
      // Whatever a gesture was holding. A disarm can land MID-DRAG (Escape
      // pressed with the pointer still down), and the moves that would have
      // finished it are gated off from this moment on.
      this._drag = null;
      this._pinch = null;
      if (this._pointers) this._pointers.clear();
      this.classList.remove('is-panning');
      this._resetZoom();
      let changed = false;
      for (const s of this.series) {
        if (s.visible) continue;
        s.visible = true;
        changed = true;
        if (s.chip) s.chip.setAttribute('aria-pressed', 'true');
        // Same rule the chip's own toggle follows: a series coming back after
        // the full-resolution fetch has already run needs its own copy.
        if (this._fullRequested) this._loadFull(s);
      }
      if (changed) this._scheduleRender();
    }

    // Wired once and for good — arming gates the HANDLERS (on `_armed`) rather
    // than the listeners, so a chart can arm and disarm all day without any of
    // this being torn down and rebuilt. The three that only ever tear state
    // DOWN (pointerup, pointerleave, blur) are deliberately left ungated: a
    // disarm that lands mid-drag still needs its `is-panning` class taken off.
    _wireEvents() {
      const svg = this.svg;
      this._pointers = new Map();

      svg.addEventListener('wheel', (e) => this._onWheel(e), { passive: false });
      svg.addEventListener('pointerdown', (e) => this._onDown(e));
      svg.addEventListener('pointermove', (e) => this._onMove(e));
      svg.addEventListener('pointerup', (e) => this._onUp(e));
      svg.addEventListener('pointercancel', (e) => this._onUp(e));
      svg.addEventListener('pointerleave', (e) => {
        if (!this._pointers.has(e.pointerId)) this._clearActive();
      });
      svg.addEventListener('dblclick', (e) => {
        if (!this._armed) return;
        e.preventDefault();
        this._resetZoom();
      });
      svg.addEventListener('keydown', (e) => this._onKey(e));
      svg.addEventListener('blur', () => this._clearActive());
    }

    // Two tiers share one frame. A FULL render re-buckets every visible series
    // and rebuilds the SVG — the answer to a domain, size, theme or data change.
    // A HIGHLIGHT frame only re-inks what the trace changed (_redrawHighlight).
    // A full request always wins a frame it shares with a highlight one, so no
    // ordering of the two can lose work.
    _scheduleRender() {
      this._rafFull = true;
      this._scheduleFrame();
    }

    _scheduleHighlight() {
      this._scheduleFrame();
    }

    _scheduleFrame() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        const full = this._rafFull;
        this._rafFull = false;
        if (full) this._render();
        else this._redrawHighlight();
      });
    }

    // ---- colours ------------------------------------------------------------

    // One resolve pass per layout/theme change, never per frame: reading
    // computed style forces a style recalc, and pans run at 60 fps.
    _resolveColors() {
      if (!this._colorsDirty || !this._probe) return;
      this._colorsDirty = false;
      const probe = this._probe;
      const read = (raw) => {
        probe.style.color = '';
        probe.style.color = raw;
        if (!probe.style.color) return null; // browser rejected the token
        return getComputedStyle(probe).color;
      };

      const baseCss = read('var(--bc-tc-line)') || read('currentColor') || 'rgb(0,0,0)';
      const bgCss = read('var(--color-background, #ffffff)') || 'rgb(255,255,255)';
      // The traced/emphasised colour. Only uniform mode and the reference rules
      // use it; it falls back to the line colour so a host that never declares
      // the token gets today's behaviour.
      this._hiColor = read('var(--bc-tc-highlight)') || baseCss;
      probe.style.color = '';

      this._bg = bgCss;
      const baseRgb = parseRgb(baseCss) || [0, 0, 0];
      const bgRgb = parseRgb(bgCss) || [255, 255, 255];
      const dark = relLuma(bgRgb) < 0.35;
      const h0 = rgbToHsl(baseRgb[0], baseRgb[1], baseRgb[2])[0];

      const paletteAt = (i) => {
        if (i === 0) return baseCss;
        const step = PALETTE_STEPS[(i - 1) % PALETTE_STEPS.length];
        // Past slot 8 there is no honest categorical answer left: nudge the hue
        // and lightness so lines stay separable-ish, and lean on the legend and
        // the direct peak labels for identity.
        const wrap = Math.floor((i - 1) / PALETTE_STEPS.length);
        const l = clamp((dark ? step.ld : step.ll) + wrap * (dark ? -0.09 : 0.11), 0.18, 0.78);
        return hsl(h0 + step.dh + wrap * 24, step.s, l);
      };

      const uniform = this.traceMode === 'uniform';
      this.series.forEach((s, i) => {
        // Uniform mode ignores the palette entirely — but still honours an
        // explicitly authored per-series colour, which is an author overriding
        // the mode on purpose.
        s.color = (s.colorAttr && read(s.colorAttr)) || (uniform ? baseCss : paletteAt(i));
        if (s.swatch) s.swatch.style.background = s.color;
      });
      probe.style.color = '';
    }

    // ---- highlight ----------------------------------------------------------

    // Public: trace the series with this key (null clears). Used by the row
    // linker at the foot of this file; safe to call before the element has
    // built, and before its islands have been read.
    //
    // opts.immediate — the highlight came from a deliberate act (a focus, a
    // blur), so speak it now rather than waiting for it to settle. Optional and
    // absent by default, which keeps the v2 one-argument call site valid.
    setHighlight(key, opts) {
      // An element whose islands were all unusable returns from
      // connectedCallback before it has a plot to render into, so "has series"
      // is the readiness test, not "is defined".
      if (!this.series || !this.series.length) return;
      // '' is a CLEAR, not a lookup that misses: a host whose Liquid conditional
      // emitted an empty key must behave exactly like one that emitted none.
      // (No series key is ever blank — they default to the series index.)
      const k = key == null || key === '' ? null : String(key);
      // A key that MISSES is a no-op, not a clear: a linked row whose curve was
      // dropped at parse time (fewer than two points) must not wipe a trace the
      // reader is holding elsewhere.
      const s = k == null ? null : this.series.find((x) => x.key === k);
      if (k != null && !s) return;
      this._setHighlight(s || null, !!(opts && opts.immediate));
    }

    // Every highlight change funnels through here so the notification can never
    // drift from the state: one guard, one redraw, one announcement, one event.
    // The redraw is the CHEAP one — nothing about a highlight moves the axes or
    // the geometry, and a pointer trace changes it on nearly every frame.
    // `source` names who set it, on the same terms as _liveOwner below: only
    // the owner of a highlight may take it away again. 'pointer' is a trace the
    // crosshair itself found; anything else (a legend chip, the public
    // setHighlight the row linker calls) is someone else's and is left alone by
    // _clearActive. Optional, so every existing two-argument call site keeps its
    // meaning — an unnamed source is simply not the pointer's.
    _setHighlight(s, immediate, source) {
      const next = s || null;
      if (this.highlight === next) {
        // Same series, new owner: a row focusing the trace the pointer already
        // found must still take ownership (else the pointer's later clear wipes
        // the focused row's trace) and still speak its immediate announcement.
        // The pointer never takes ownership this way -- a sweep across an
        // already-focused trace must not demote it to pointer-owned.
        if (next && source !== 'pointer') {
          this._hiSource = source || 'external';
          if (immediate === true) this._announce(next, true);
        }
        return;
      }
      this.highlight = next;
      this._hiSource = this.highlight ? source || 'external' : null;
      this._scheduleHighlight();
      this._announce(this.highlight, immediate === true);
      this.dispatchEvent(new CustomEvent('bc:test-chart:highlight', {
        bubbles: true,
        detail: { key: this.highlight ? this.highlight.key : null },
      }));
    }

    // The traced series is stated visually (brightened trace, marked row) and
    // nowhere else, which leaves a keyboard reader tabbing the specimen table
    // with no idea what just happened. Say it in the live region the crosshair
    // readout already uses — sharing it deliberately, since the two are the same
    // reader's attention and must not talk over each other.
    _announce(s, immediate) {
      if (!this.live) return;
      clearTimeout(this._liveT);
      this._liveT = null;
      const say = () => {
        this._liveT = null;
        if (!this.live) return;
        if (!s) {
          // Only take the region back if what is in it is ours: by now the
          // crosshair may have written a readout there, and blanking someone
          // else's message is worse than leaving a stale one.
          if (this._liveOwner !== 'highlight') return;
          this.live.textContent = '';
          this._liveOwner = null;
          return;
        }
        this.live.textContent =
          'Tracing ' + s.label + ', peak ' + fmtTrim(s.peak.value, 2) + ' ' + this.units + '.';
        this._liveOwner = 'highlight';
      };
      if (immediate) say();
      else this._liveT = setTimeout(say, HIGHLIGHT_SAY_MS);
    }

    // The series whose value at `t` is nearest the pointer, within a small band.
    // Outside the band nothing is traced — a reader between two curves is not
    // pointing at either.
    _traceNearest(clientY, t) {
      if (!this._geom || clientY == null || !this.svg) return;
      const rect = this.svg.getBoundingClientRect();
      const scale = this._H / (rect.height || this._H) || 1;
      const yv = (clientY - rect.top) * scale;
      const BAND = 14;
      let best = null;
      let bestD = Infinity;
      let nextD = Infinity;
      for (const s of this._visible()) {
        const p = s.points[nearestIndex(s.points, t)];
        if (!p) continue;
        const d = Math.abs(this._y(p[1]) - yv);
        if (d < bestD) {
          nextD = bestD;
          bestD = d;
          best = s;
        } else if (d < nextD) {
          nextD = d;
        }
      }
      if (!best || bestD >= BAND) {
        // Only clear a highlight the pointer owns -- the empty space between
        // curves must not wipe a trace a focused row or legend chip is holding.
        if (!this._hiSource || this._hiSource === 'pointer') {
          this._setHighlight(null, undefined, 'pointer');
        }
        return;
      }
      // The specimens of one configuration break within a couple of kN of each
      // other, so the two nearest traces are routinely a sub-pixel apart and
      // "nearest" flips on the smallest twitch. Demand a clear winner — the
      // runner-up at least half again as far — and otherwise leave the current
      // trace ALONE: holding it is honest about the ambiguity, where flickering
      // (or clearing) reads as a glitch.
      if (nextD < bestD * 1.5) return;
      this._setHighlight(best, undefined, 'pointer');
    }

    // ---- render -------------------------------------------------------------

    _visible() {
      return this.series.filter((s) => s.visible);
    }

    _render() {
      const W = Math.round(this.plot.clientWidth || this.plot.getBoundingClientRect().width || 0);
      if (!W || !this.svg) return;
      const H = Math.max(200, Math.min(360, Math.round(W * 0.5)));

      this._resolveColors();

      this._W = W;
      this._H = H;

      const ML = 52;
      const MR = 16;
      const MT = 14;
      const MB = 38;
      const innerW = Math.max(10, W - ML - MR);
      const innerH = Math.max(10, H - MT - MB);
      this._geom = { ML, MT, innerW, innerH };

      const t0 = this.view.t0;
      const t1 = this.view.t1;
      const tSpan = t1 - t0 || 1;

      // --- bucket every visible series to ~2 points per pixel ---
      const nBuckets = Math.max(2, Math.round(innerW));
      const drawn = [];
      let vMin = Infinity;
      let vMax = -Infinity;
      for (const s of this._visible()) {
        const b = bucketize(s.points, t0, t1, nBuckets);
        // dot/label are filled in by the peak-marker pass at the foot of this
        // method and kept so the highlight-only redraw can re-ink them in place
        // instead of rebuilding the SVG for a change of emphasis.
        drawn.push({ s: s, flat: b.flat, dot: null, label: null });
        if (!b.empty) {
          if (b.vMin < vMin) vMin = b.vMin;
          if (b.vMax > vMax) vMax = b.vMax;
          // A declared peak is sample-exact; keep it inside the axis whenever it
          // falls in the window, even if the bucketing rounded past it.
          if (s.peak.t >= t0 && s.peak.t <= t1 && s.peak.value > vMax) vMax = s.peak.value;
        }
      }
      if (vMin === Infinity) {
        vMin = 0;
        vMax = 1;
      }
      // A reference rule outside the axis would silently vanish, which is worse
      // than a slightly taller plot: fold them into the extent before it is
      // rounded to nice bounds. Only while the rule is anywhere near what is on
      // screen, though — zoomed into the 0–2 kN toe of a 30 kN break, folding in
      // the 3σ MBS would scale the view back to 30 kN and flatten the very
      // detail the zoom was for. Past 3× the visible peak the rule and its label
      // are simply not drawn for this view (the rule loop below already skips
      // anything off-axis); resetting the zoom brings them back. At full domain
      // the visible peak is the data peak, and a mean or a 3σ MBS is at or below
      // it by construction, so the full view is exactly what it always was.
      const dataMax = vMax;
      for (const r of this.refs) {
        if (dataMax > 0 && r.value > dataMax * 3) continue;
        if (r.value < vMin) vMin = r.value;
        if (r.value > vMax) vMax = r.value;
      }

      // The FORCE window. Held by the reader (a wheel/pinch zoom or a drag on
      // the y axis) it is used verbatim, with a tick step derived to suit it;
      // otherwise the axis expands to nice bounds around the data in view — the
      // resting behaviour, and what every chart showed before the y axis could
      // be zoomed at all. See _niceYBounds for the rounding policy.
      let yLo;
      let yHi;
      let yStep;
      if (this.viewY) {
        yLo = this.viewY.v0;
        yHi = this.viewY.v1;
        // A HIGHER tick target than the fitted axis asks for, because this one
        // cannot round its bounds outward to meet the step: niceStep only ever
        // rounds a step UP, so an unrounded range gets between T/2 and T ticks.
        // At the fitted axis's target of 5 that bottoms out at two gridlines
        // across the whole plot; the time axis has always used 6 for exactly
        // this reason — its bounds are the view's too.
        yStep = niceStep(yHi - yLo, 6);
      } else {
        const b = this._niceYBounds(vMin, vMax);
        yLo = b.lo;
        yHi = b.hi;
        yStep = b.step;
      }
      const yDec = decimalsForStep(yStep);
      this._yLo = yLo;
      this._yHi = yHi;

      const xStep = niceStep(tSpan, W < 460 ? 4 : 6);
      const xDec = decimalsForStep(xStep);

      const x = (t) => ML + ((t - t0) / tSpan) * innerW;
      const y = (v) => MT + innerH - ((v - yLo) / (yHi - yLo || 1)) * innerH;
      this._x = x;
      this._y = y;
      this._dec = {
        t: Math.max(xDec, decimalsForSpan(tSpan)),
        // Zoomed into the force axis the readout needs the places the span asks
        // for, exactly as the time readout already does. Off a held window the
        // expression is the one it always was, so nothing moves at rest.
        v: Math.max(yDec, 2, this.viewY ? decimalsForSpan(yHi - yLo) : 0),
      };

      // --- SVG frame ---
      this.svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      while (this.svg.firstChild) this.svg.removeChild(this.svg.firstChild);

      // Fitted, the low bound IS a whole tick step, so the ticks start on it —
      // written as the literal bound rather than a rounding of it so the
      // un-zoomed axis is bit-for-bit what it always drew. A held window starts
      // wherever it likes, so the first tick is the first multiple inside it.
      const yStart = this.viewY ? Math.ceil(yLo / yStep - 1e-9) * yStep : yLo;
      for (let v = yStart; v <= yHi + yStep * 0.001; v += yStep) {
        const yy = y(v);
        this.svg.appendChild(svgEl('line', {
          class: 'bc-test-chart__gridline',
          x1: ML, y1: yy, x2: ML + innerW, y2: yy, 'stroke-width': 1,
        }));
        const lbl = svgEl('text', {
          class: 'bc-test-chart__axis-label',
          x: ML - 8, y: yy + 4, 'text-anchor': 'end', 'font-size': 11,
        });
        lbl.textContent = fmt(v, yDec);
        this.svg.appendChild(lbl);
      }

      const firstTick = Math.ceil(t0 / xStep) * xStep;
      for (let t = firstTick; t <= t1 + xStep * 0.001; t += xStep) {
        const xx = x(t);
        this.svg.appendChild(svgEl('line', {
          class: 'bc-test-chart__gridline',
          x1: xx, y1: MT, x2: xx, y2: MT + innerH, 'stroke-width': 1, 'stroke-opacity': 0.5,
        }));
        const lbl = svgEl('text', {
          class: 'bc-test-chart__axis-label',
          x: xx, y: MT + innerH + 17, 'text-anchor': 'middle', 'font-size': 11,
        });
        lbl.textContent = fmt(t, xDec);
        this.svg.appendChild(lbl);
      }

      const xTitle = svgEl('text', {
        class: 'bc-test-chart__axis-title',
        x: ML + innerW / 2, y: H - 3, 'text-anchor': 'middle',
        'font-size': 11, 'font-weight': 600,
      });
      xTitle.textContent = 'Time (s)';
      this.svg.appendChild(xTitle);

      const yTitle = svgEl('text', {
        class: 'bc-test-chart__axis-title',
        x: 0, y: 0, 'text-anchor': 'middle',
        'font-size': 11, 'font-weight': 600,
        transform: `translate(12 ${MT + innerH / 2}) rotate(-90)`,
      });
      yTitle.textContent = 'Force (' + this.units + ')';
      this.svg.appendChild(yTitle);

      // --- reference rules (mean / 3σ MBS) ---
      // Drawn in the SVG layer, which sits ABOVE the canvas traces: these are
      // the two figures the record is arguing about, so they read as annotation
      // over the data rather than as another curve under it. Each carries its
      // value AND its unit, since nothing else on the plot states one.
      if (this.refs.length) {
        const placed = [];
        for (const r of this.refs) {
          const ry = y(r.value);
          if (ry < MT - 1 || ry > MT + innerH + 1) continue;
          this.svg.appendChild(svgEl('line', {
            class: 'bc-test-chart__ref bc-test-chart__ref--' + r.kind,
            x1: ML, y1: ry, x2: ML + innerW, y2: ry, 'stroke-width': r.kind === 'mbs' ? 1.4 : 1.2,
          }));
          // Labels alternate left/right, and a second label that would land on
          // the first is pushed under its rule instead of over it.
          const right = placed.length > 0;
          const clash = placed.some((p) => Math.abs(p - ry) < 13);
          const ly = clash ? ry + 12 : ry - 5;
          const lbl = svgEl('text', {
            class: 'bc-test-chart__ref-label bc-test-chart__ref-label--' + r.kind,
            x: right ? ML + innerW - 3 : ML + 4,
            y: clamp(ly, MT + 9, MT + innerH - 2),
            'text-anchor': right ? 'end' : 'start',
            'font-size': 11, 'font-weight': 600,
          });
          lbl.textContent = r.label + ' ' + fmtTrim(r.value, 2) + ' ' + this.units;
          this.svg.appendChild(lbl);
          placed.push(ry);
        }
      }

      // --- traces (canvas) ---
      // This bucketing is the cache the highlight redraw runs on: it is exact
      // until the view, the size, the visibility or the points change, and every
      // one of those goes through a full render.
      this._drawn = drawn;
      this._drawTraces(drawn);

      // --- peak markers: the only values printed on the plot ---
      // At most four direct labels; past that the legend carries the peaks and
      // the plot keeps only the dots, so labels can never pile up on each other.
      // data-peak-labels="off" drops them at any count, for a host whose table
      // already prints every peak (see the header comment).
      const labelPeaks = this.peakLabels && drawn.length <= 4;
      const uniform = this.traceMode === 'uniform';
      for (const d of drawn) {
        const s = d.s;
        if (s.peak.t < t0 || s.peak.t > t1) continue;
        const px = x(s.peak.t);
        const py = y(s.peak.value);
        if (py < MT - 4 || py > MT + innerH + 4) continue;
        const traced = this.highlight === s;
        const dot = svgEl('circle', {
          class: 'bc-test-chart__peak-dot',
          cx: px, cy: py, r: 4, 'stroke-width': 2,
        });
        dot.style.fill = uniform && traced ? this._hiColor : s.color;
        if (this.highlight && !traced) dot.setAttribute('opacity', uniform ? '0.18' : '0.3');
        this.svg.appendChild(dot);
        d.dot = dot;

        if (!labelPeaks) continue;
        const flip = px > ML + innerW * 0.72;
        const plabel = svgEl('text', {
          class: 'bc-test-chart__peak-label',
          x: flip ? px - 9 : px + 9,
          y: Math.max(MT + 10, py - 9),
          'text-anchor': flip ? 'end' : 'start',
          'font-size': 11, 'font-weight': 700,
        });
        plabel.textContent = fmtTrim(s.peak.value, 2) + ' ' + this.units;
        if (this.highlight && this.highlight !== s) plabel.setAttribute('opacity', '0.3');
        this.svg.appendChild(plabel);
        d.label = plabel;
      }

      this.crosshair = svgEl('g', { class: 'bc-test-chart__crosshair', 'aria-hidden': 'true' });
      this.svg.appendChild(this.crosshair);

      if (this.activeT != null) this._showActiveAt(this.activeT, true);
    }

    /*
     * Highlight-only redraw. Tracing a pointer along five 20k-point curves (or
     * down a specimen table) changes exactly three things: which trace is drawn
     * bright and last, which peak dot is inked in the highlight colour, and which
     * markers are held back. Nothing moves the axes, the ticks, the reference
     * rules or the buckets — so running a full _render per pointer frame spends
     * a re-bucket of every series and a from-scratch SVG rebuild to change an
     * opacity. This reuses the geometry the last full render cached and touches
     * only what the highlight owns.
     *
     * The result is identical to what _render would have produced, and it stays
     * that way by keeping the two writes to the peak markers in step: whatever
     * _render sets there, this sets or REMOVES here (an absent attribute is what
     * a freshly built marker has). Anything that could invalidate the cache goes
     * through _scheduleRender, and an unusable cache falls back to _render.
     */
    _redrawHighlight() {
      if (!this._drawn || !this._geom || !this.ctx || !this.svg) {
        this._render();
        return;
      }

      this._drawTraces(this._drawn);

      const uniform = this.traceMode === 'uniform';
      for (const d of this._drawn) {
        const traced = this.highlight === d.s;
        if (d.dot) {
          d.dot.style.fill = uniform && traced ? this._hiColor : d.s.color;
          if (this.highlight && !traced) d.dot.setAttribute('opacity', uniform ? '0.18' : '0.3');
          else d.dot.removeAttribute('opacity');
        }
        if (d.label) {
          if (this.highlight && !traced) d.label.setAttribute('opacity', '0.3');
          else d.label.removeAttribute('opacity');
        }
      }
    }

    _drawTraces(drawn) {
      const ctx = this.ctx;
      if (!ctx) return;
      const { ML, MT, innerW, innerH } = this._geom;
      const W = this._W;
      const H = this._H;
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      const pw = Math.round(W * dpr);
      const ph = Math.round(H * dpr);
      if (this.canvas.width !== pw || this.canvas.height !== ph) {
        this.canvas.width = pw;
        this.canvas.height = ph;
      }
      ctx.setTransform(pw / W, 0, 0, ph / H, 0, 0);
      ctx.clearRect(0, 0, W, H);

      ctx.save();
      ctx.beginPath();
      ctx.rect(ML, MT, innerW, innerH);
      ctx.clip();
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';

      // Highlighted series last so it sits on top of the dimmed ones.
      const order = this.highlight
        ? drawn.slice().sort((a, b) => (a.s === this.highlight ? 1 : 0) - (b.s === this.highlight ? 1 : 0))
        : drawn;

      // Uniform mode: one ink for every trace, held back far enough that five
      // overlaid breaks read as a band rather than a tangle, with the traced one
      // brought forward in the highlight colour. Categorical mode keeps each
      // series' own colour and only dims the rest.
      const uniform = this.traceMode === 'uniform';
      for (const d of order) {
        const flat = d.flat;
        if (flat.length < 4) continue;
        const traced = this.highlight === d.s;
        if (uniform) {
          ctx.globalAlpha = this.highlight ? (traced ? 1 : 0.16) : 0.46;
          ctx.strokeStyle = traced ? this._hiColor : d.s.color;
          ctx.lineWidth = traced ? 2.6 : 1.6;
        } else {
          ctx.globalAlpha = this.highlight && !traced ? 0.22 : 1;
          ctx.strokeStyle = d.s.color;
          ctx.lineWidth = 2;
        }
        ctx.beginPath();
        ctx.moveTo(this._x(flat[0]), this._y(flat[1]));
        for (let i = 2; i < flat.length; i += 2) {
          ctx.lineTo(this._x(flat[i]), this._y(flat[i + 1]));
        }
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    }

    // ---- pointer ------------------------------------------------------------

    _timeFromClientX(clientX) {
      const rect = this.svg.getBoundingClientRect();
      const scale = this._W / (rect.width || this._W) || 1;
      const xv = (clientX - rect.left) * scale;
      const { ML, innerW } = this._geom;
      const frac = clamp((xv - ML) / innerW, 0, 1);
      return this.view.t0 + frac * (this.view.t1 - this.view.t0);
    }

    // Time-per-CSS-pixel, for turning a drag distance into a pan.
    _timePerPx() {
      const rect = this.svg.getBoundingClientRect();
      const scale = this._W / (rect.width || this._W) || 1;
      return ((this.view.t1 - this.view.t0) / this._geom.innerW) * scale;
    }

    // The force under the cursor — the y axis's _timeFromClientX, and the
    // anchor a 2D wheel/pinch zoom pins. Clamped to the plot rect for the same
    // reason: a gesture that starts on the margin still means the nearest
    // point of the data.
    _valueFromClientY(clientY) {
      const eff = this._effectiveY();
      if (clientY == null || !this.svg || !this._geom) return (eff.v0 + eff.v1) / 2;
      const rect = this.svg.getBoundingClientRect();
      const scale = this._H / (rect.height || this._H) || 1;
      const yv = (clientY - rect.top) * scale;
      const { MT, innerH } = this._geom;
      const frac = clamp((yv - MT) / innerH, 0, 1);
      // Screen y runs down, force runs up.
      return eff.v1 - frac * (eff.v1 - eff.v0);
    }

    // Separation of two touch points, floored so a factor is never a division
    // by zero.
    _pinchDist(p) {
      const dx = p[0].x - p[1].x;
      const dy = p[0].y - p[1].y;
      return Math.max(1, Math.sqrt(dx * dx + dy * dy));
    }

    // Force-per-CSS-pixel, for turning a drag distance into a pan.
    _valuePerPx() {
      const eff = this._effectiveY();
      const rect = this.svg.getBoundingClientRect();
      const scale = this._H / (rect.height || this._H) || 1;
      return ((eff.v1 - eff.v0) / this._geom.innerH) * scale;
    }

    _onWheel(e) {
      // FIRST, and before anything that could preventDefault: an unarmed chart
      // does not touch the wheel at all, so the page scrolls straight past it
      // (the listener is non-passive only for the armed case). Not even the
      // "ctrl + scroll to zoom" hint — there is nothing to zoom yet.
      if (!this._armed) return;
      if (!this._geom) return;
      // At 1× the wheel belongs to the page — entering zoom takes ctrl/⌘+wheel
      // (which is also how browsers deliver a trackpad pinch). Without the
      // modifier, show the transient hint and let the page scroll. Once zoomed,
      // the chart owns the wheel outright.
      const modified = e.ctrlKey || e.metaKey;
      if (!this._isZoomed() && !modified) {
        this._showZoomHint();
        return;
      }
      // BOTH AXES by default — a wheel zoom is a zoom INTO the point under the
      // cursor, not a stretch of the time axis alone. The two single-axis
      // gestures are modifiers on that: shift for time, alt/option for force.
      // (Shift+wheel is delivered as deltaX by most browsers, which is why the
      // delta is read from whichever axis actually carries it.)
      const axes = e.shiftKey ? 'x' : e.altKey ? 'y' : 'both';
      const dRaw = e.deltaY || e.deltaX || 0;
      const factor = Math.pow(1.0016, -dRaw * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1));
      const moved = this._zoomBy(
        clamp(factor, 0.2, 5),
        this._timeFromClientX(e.clientX),
        this._valueFromClientY(e.clientY),
        axes
      );
      // Swallow no-op MODIFIED wheels too: ctrl+wheel's browser default is page
      // zoom, which is never what a reader pinching at the clamp intends.
      if (moved || modified) e.preventDefault();
    }

    _showZoomHint() {
      if (!this._zoomHint) return;
      this._zoomHint.classList.add('is-visible');
      clearTimeout(this._zoomHintT);
      this._zoomHintT = setTimeout(() => {
        this._zoomHint.classList.remove('is-visible');
      }, 1300);
    }

    _onDown(e) {
      // Unarmed, the press belongs to the arm overlay above the plot (and to
      // the page's own scroll on touch); nothing here may claim it.
      if (!this._armed) return;
      if (!this._geom) return;
      this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this._pointers.size === 2) {
        const p = Array.from(this._pointers.values());
        this._pinch = {
          // The TRUE separation, not its horizontal component: a pinch scales
          // both axes together, so the gesture has to be measured the way it is
          // made. A near-horizontal pinch still reads as it always did (the
          // hypotenuse is its dx), and a vertical one now means something.
          dist: this._pinchDist(p),
          midT: this._timeFromClientX((p[0].x + p[1].x) / 2),
          midV: this._valueFromClientY((p[0].y + p[1].y) / 2),
        };
        this._drag = null;
        this._clearActive();
        return;
      }
      if (this._pointers.size > 2) return;

      // Double-tap reset (touch has no dblclick).
      if (e.pointerType === 'touch') {
        const now = Date.now();
        if (this._lastTap && now - this._lastTap.t < 320 &&
            Math.abs(e.clientX - this._lastTap.x) < 24 && Math.abs(e.clientY - this._lastTap.y) < 24) {
          this._lastTap = null;
          this._resetZoom();
          return;
        }
        this._lastTap = { t: now, x: e.clientX, y: e.clientY };
      }

      // Drag-pan only exists once zoomed — on EITHER axis. That is also what
      // keeps one-finger touch scrolling the page at rest (see .is-zoomed /
      // touch-action). `panX`/`panY` record which axes actually have somewhere
      // to go, so the slop test below only counts movement that can pan: with
      // the force axis fitted, a vertical drag still reads the trace exactly as
      // it did before this axis could be zoomed.
      if (this._isZoomed()) {
        this._drag = {
          x: e.clientX,
          y: e.clientY,
          t0: this.view.t0,
          v0: this.viewY ? this.viewY.v0 : null,
          panX: this._zoom() > 1.001,
          panY: !!this.viewY,
          moved: false,
          id: e.pointerId,
        };
      } else {
        this._drag = null;
        this._onPointerRead(e);
      }
    }

    _onMove(e) {
      if (!this._armed) return;
      if (!this._geom) return;
      if (this._pointers.has(e.pointerId)) this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this._pinch && this._pointers.size >= 2) {
        const p = Array.from(this._pointers.values());
        const dist = this._pinchDist(p);
        const factor = dist / this._pinch.dist;
        if (Math.abs(factor - 1) > 0.01) {
          this._zoomBy(factor, this._pinch.midT, this._pinch.midV, 'both');
          this._pinch.dist = dist;
          this._pinch.midT = this._timeFromClientX((p[0].x + p[1].x) / 2);
          this._pinch.midV = this._valueFromClientY((p[0].y + p[1].y) / 2);
        }
        e.preventDefault();
        return;
      }

      if (this._drag) {
        const dx = e.clientX - this._drag.x;
        const dy = e.clientY - this._drag.y;
        // Only the axes that can pan count toward the slop, so a drag that
        // cannot move anything never becomes a pan (and keeps reading the
        // trace instead).
        const slopX = this._drag.panX ? dx : 0;
        const slopY = this._drag.panY ? dy : 0;
        if (!this._drag.moved && Math.sqrt(slopX * slopX + slopY * slopY) < DRAG_SLOP) return;
        if (!this._drag.moved) {
          this._drag.moved = true;
          this._clearActive();
          this.classList.add('is-panning');
          if (this.svg.setPointerCapture) {
            try {
              this.svg.setPointerCapture(e.pointerId);
            } catch (err) {
              /* capture is a nicety, not a requirement */
            }
          }
        }
        // The content follows the pointer on both axes: dragging right brings
        // earlier times in from the left, dragging down brings higher forces in
        // from the top (screen y runs down, force runs up).
        let moved = false;
        if (this._drag.panX) {
          const span = this.view.t1 - this.view.t0;
          const t0 = clamp(this._drag.t0 - dx * this._timePerPx(), this.domain.t0, this.domain.t1 - span);
          if (Math.abs(t0 - this.view.t0) > 1e-12) {
            this.view = { t0: t0, t1: t0 + span };
            moved = true;
          }
        }
        if (this._drag.panY && this.viewY && this.domainY) {
          const spanV = this.viewY.v1 - this.viewY.v0;
          const v0 = clamp(
            this._drag.v0 + dy * this._valuePerPx(),
            this.domainY.v0,
            this.domainY.v1 - spanV
          );
          if (Math.abs(v0 - this.viewY.v0) > 1e-12) {
            this.viewY = { v0: v0, v1: v0 + spanV };
            moved = true;
          }
        }
        if (moved) {
          this._onInteract();
          this._scheduleRender();
        }
        e.preventDefault();
        return;
      }

      this._onPointerRead(e);
    }

    _onUp(e) {
      this._pointers.delete(e.pointerId);
      if (this._pointers.size < 2) this._pinch = null;
      if (this._drag && this._drag.id === e.pointerId) {
        this.classList.remove('is-panning');
        this._drag = null;
      }
    }

    // Dropping kbIndex hands the keyboard walk back to wherever the pointer
    // left off, instead of resuming from a stale index further down the trace.
    _onPointerRead(e) {
      this.kbIndex = null;
      const t = this._timeFromClientX(e.clientX);
      this._showActiveAt(t);
      // Pointing AT a curve traces it (and, through the linker, its row) — in
      // UNIFORM mode only. There the trace is the whole identity affordance;
      // in the categorical default every series already carries its own colour,
      // so dimming the rest on a plain hover would be a behaviour change for
      // every host that never asked for the trace.
      if (this.traceMode === 'uniform') this._traceNearest(e.clientY, t);
    }

    // ---- keyboard -----------------------------------------------------------

    _primary() {
      const v = this._visible();
      return v.length ? v[0] : this.series[0];
    }

    _onKey(e) {
      // Kept as two, because they drive different keys: the time window is what
      // the left/right arrows pan and what the sample walk steps through, the
      // force window is what up/down pan.
      const zoomed = this._zoom() > 1.001;
      const zoomedY = this._zoomY() > 1.001;
      const key = e.key;

      // Unarmed the plot drives nothing — and it is not a tab stop either, so
      // this is the safety net for a focus that arrives some other way (a
      // script, a find-in-page landing). Enter/Space is the keyboard's press.
      if (!this._armed) {
        if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
          e.preventDefault();
          this._arm();
        }
        return;
      }

      // Both axes about the middle of what is on screen — the keyboard's
      // version of a wheel zoom at the centre of the plot.
      if (key === '+' || key === '=') {
        e.preventDefault();
        this._zoomBy(1.6, (this.view.t0 + this.view.t1) / 2, null, 'both');
        return;
      }
      if (key === '-' || key === '_') {
        e.preventDefault();
        this._zoomBy(1 / 1.6, (this.view.t0 + this.view.t1) / 2, null, 'both');
        return;
      }
      if (key === '0') {
        e.preventDefault();
        this._resetZoom();
        return;
      }
      if (key === 'Escape') {
        this._clearActive();
        // …and, on an arm-mode chart, the way out. The document-level listener
        // at the foot of this file catches an Escape pressed with focus
        // anywhere else; this is the same act arriving at the plot first, and
        // the two are idempotent. A chart without data-arm ignores it.
        this.releaseIfArmed();
        return;
      }

      const horizontal = key === 'ArrowLeft' || key === 'ArrowRight';
      const vertical = key === 'ArrowUp' || key === 'ArrowDown';
      if (!horizontal && !vertical && key !== 'Home' && key !== 'End') return;

      // Zoomed, the arrows drive the window; shift+arrow still steps samples so
      // the readout never becomes unreachable. Each axis answers to its own
      // pair, and only while it has somewhere to go — with the force axis
      // fitted to the data, up/down keep stepping samples exactly as they did
      // before this axis could be zoomed.
      if (zoomed && horizontal && !e.shiftKey) {
        e.preventDefault();
        const span = this.view.t1 - this.view.t0;
        this._panBy((key === 'ArrowRight' ? 0.2 : -0.2) * span);
        return;
      }
      if (zoomedY && vertical && !e.shiftKey) {
        e.preventDefault();
        const spanV = this.viewY.v1 - this.viewY.v0;
        this._panByV((key === 'ArrowUp' ? 0.2 : -0.2) * spanV);
        return;
      }

      const s = this._primary();
      const pts = s.points;
      const n = pts.length;
      // ~60 presses to traverse the visible window; shift walks sample by sample.
      const lo = nearestIndex(pts, this.view.t0);
      const hi = nearestIndex(pts, this.view.t1);
      const stride = e.shiftKey && !zoomed ? 1 : Math.max(1, Math.round(Math.abs(hi - lo) / 60));

      let idx = this.kbIndex;
      if (idx == null || idx < 0) idx = key === 'ArrowLeft' || key === 'ArrowDown' || key === 'End' ? hi : lo - 1;
      if (key === 'ArrowRight' || key === 'ArrowUp') idx = Math.min(hi, idx + stride);
      else if (key === 'ArrowLeft' || key === 'ArrowDown') idx = Math.max(lo, idx - stride);
      else if (key === 'Home') idx = lo;
      else idx = hi;
      idx = clamp(idx, 0, n - 1);

      e.preventDefault();
      this.kbIndex = idx;
      this._showActiveAt(pts[idx][0]);
    }

    // ---- readout ------------------------------------------------------------

    _clearActive() {
      this.activeT = null;
      this.kbIndex = null;
      // The pointer-driven trace goes with the readout that produced it — and
      // ONLY that one. By the time this runs the highlight may belong to a
      // focused specimen row or a legend chip, neither of which has anything to
      // do with the pointer leaving the plot, and clearing it there took away
      // someone else's trace: a keyboard reader on a row watched it vanish
      // because the mouse happened to cross the chart. Same identity guard the
      // legend chips and the row linker carry, expressed as ownership because
      // this side has no single `s` to compare against.
      if (this._hiSource === 'pointer') this._setHighlight(null, undefined, 'pointer');
      if (this.crosshair) {
        while (this.crosshair.firstChild) this.crosshair.removeChild(this.crosshair.firstChild);
      }
      if (this.tooltip) this.tooltip.classList.remove('is-visible');
    }

    // Nearest sample across the visible series anchors the crosshair; every
    // visible series then reports its own value at that time.
    _showActiveAt(t, silent) {
      if (!this._geom || !this.crosshair) return;
      const vis = this._visible();
      if (!vis.length) return;

      let anchor = null;
      const rows = [];
      for (const s of vis) {
        const i = nearestIndex(s.points, t);
        const p = s.points[i];
        if (!p) continue;
        rows.push({ s: s, p: p });
        const d = Math.abs(p[0] - t);
        if (!anchor || d < anchor.d) anchor = { d: d, p: p };
      }
      if (!anchor) return;

      const at = anchor.p[0];
      this.activeT = at;
      if (this.kbIndex == null) this.kbIndex = nearestIndex(this._primary().points, at);

      const { MT, innerH } = this._geom;
      const xx = this._x(at);
      while (this.crosshair.firstChild) this.crosshair.removeChild(this.crosshair.firstChild);
      this.crosshair.appendChild(svgEl('line', {
        class: 'bc-test-chart__crosshair-line',
        x1: xx, y1: MT, x2: xx, y2: MT + innerH, 'stroke-width': 1, 'stroke-dasharray': '3 3',
      }));

      let topY = Infinity;
      for (const r of rows) {
        const i = nearestIndex(r.s.points, at);
        const p = r.s.points[i];
        r.p = p;
        const yy = this._y(p[1]);
        if (yy < MT - 6 || yy > MT + innerH + 6) continue;
        const dot = svgEl('circle', {
          class: 'bc-test-chart__crosshair-dot',
          cx: xx, cy: yy, r: 4, 'stroke-width': 2,
        });
        dot.style.fill = r.s.color;
        this.crosshair.appendChild(dot);
        if (yy < topY) topY = yy;
      }
      if (topY === Infinity) topY = MT;

      this._updateTooltip(at, rows, xx, topY, silent);
    }

    _updateTooltip(t, rows, xx, yy, silent) {
      if (!this.tooltip) return;
      const tTxt = fmt(t, this._dec.t) + ' s';

      this.tooltip.innerHTML = '';
      const head = htmlEl('p', 'bc-test-chart__tooltip-time');
      head.textContent = tTxt;
      this.tooltip.appendChild(head);

      const shown = rows.slice(0, TOOLTIP_MAX_ROWS);
      if (!this.multi) {
        const val = htmlEl('p', 'bc-test-chart__tooltip-val');
        val.textContent = fmt(shown[0].p[1], this._dec.v) + ' ' + this.units;
        this.tooltip.appendChild(val);
      } else {
        const list = htmlEl('ul', 'bc-test-chart__tooltip-rows');
        for (const r of shown) {
          const li = htmlEl('li', 'bc-test-chart__tooltip-row');
          const sw = htmlEl('span', 'bc-test-chart__tooltip-swatch');
          sw.style.background = r.s.color;
          const name = htmlEl('span', 'bc-test-chart__tooltip-name');
          name.textContent = r.s.label;
          const val = htmlEl('span', 'bc-test-chart__tooltip-val');
          val.textContent = fmt(r.p[1], this._dec.v) + ' ' + this.units;
          li.appendChild(sw);
          li.appendChild(name);
          li.appendChild(val);
          list.appendChild(li);
        }
        this.tooltip.appendChild(list);
        if (rows.length > shown.length) {
          const more = htmlEl('p', 'bc-test-chart__tooltip-more');
          more.textContent = '+' + (rows.length - shown.length) + ' more';
          this.tooltip.appendChild(more);
        }
      }
      this.tooltip.classList.add('is-visible');

      if (!silent && this.live) {
        this.live.textContent = tTxt + '. ' + shown
          .map((r) => (this.multi ? r.s.label + ' ' : '') + fmt(r.p[1], this._dec.v) + ' ' + this.units)
          .join(', ') + (rows.length > shown.length ? ', and ' + (rows.length - shown.length) + ' more' : '');
        // The readout now owns the region; a highlight clear settling after this
        // must not wipe it (see _announce).
        this._liveOwner = 'readout';
      }

      // Track the point rather than pinning to the top of the plot: a pinned
      // tooltip sits exactly where the peak's direct label lives and covers it.
      const tipW = this.tooltip.offsetWidth || 110;
      const tipH = this.tooltip.offsetHeight || 46;
      let left = xx - tipW / 2;
      left = clamp(left, 0, Math.max(0, this._W - tipW));
      let top = yy - tipH - 12;
      if (top < 0) top = Math.min(Math.max(0, this._H - tipH), yy + 14);
      this.tooltip.style.left = left + 'px';
      this.tooltip.style.top = Math.max(0, top) + 'px';
    }
  }

  customElements.define('bc-test-chart', BcTestChart);

  // ---- row ↔ curve linker ---------------------------------------------------
  //
  // Deliberately OUTSIDE the element (see ROW LINKING in the header comment):
  // the element never reads the page, and the host never reads the element's
  // internals. All that crosses between them is a key.
  //
  // The link container is the scope for BOTH sides, so a page may carry any
  // number of independent table+chart pairs — the batch report page has one per
  // test configuration.

  const LINKED = 'bcChartLinked';

  function linkRows(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const groups = scope.querySelectorAll('[data-bc-chart-link]');
    for (const group of groups) {
      const chart = group.querySelector('bc-test-chart');
      // The flag rides the CHART, not the container: a theme-editor reload or a
      // morph can swap a fresh chart in under a container that has already been
      // marked, and that new element still needs wiring. A blank key is skipped
      // outright (setHighlight treats '' as a clear, so it could never match a
      // row), and rows are claimed by their NEAREST link container, so nested
      // containers bind each row exactly once.
      if (!chart || chart.dataset[LINKED]) continue;
      const rows = Array.from(group.querySelectorAll('[data-bc-series]')).filter(
        (row) => row.getAttribute('data-bc-series') &&
          row.closest('[data-bc-chart-link]') === group
      );
      if (!rows.length) continue;
      chart.dataset[LINKED] = '1';

      const byKey = new Map();
      for (const row of rows) byKey.set(row.getAttribute('data-bc-series'), row);

      let current = null;
      const mark = (key) => {
        if (current === key) return;
        const was = current == null ? null : byKey.get(current);
        if (was) was.classList.remove('is-traced');
        current = key == null ? null : String(key);
        const now = current == null ? null : byKey.get(current);
        if (now) now.classList.add('is-traced');
        // On the container so the host can recede everything else in one rule.
        group.classList.toggle('is-tracing', !!now);
      };

      for (const row of rows) {
        const key = row.getAttribute('data-bc-series');
        // setHighlight fires the same event a pointer trace does, so the row's
        // own .is-traced comes back through `mark` — one code path either way.
        // Focus is a deliberate landing (a keyboard reader walking the table),
        // so it is announced at once; a pointer sweeping the rows settles first
        // or every row it crosses would be spoken.
        const on = (immediate) =>
          chart.setHighlight && chart.setHighlight(key, { immediate: immediate });
        // Clear only what this row still owns. By the time a leave or blur
        // arrives the highlight may already have moved to another row, a legend
        // chip or a pointer trace, and blanking it then would take away someone
        // else's — the same identity guard the legend chips carry.
        const off = (immediate) => {
          if (current !== key) return;
          chart.setHighlight && chart.setHighlight(null, { immediate: immediate });
        };
        row.addEventListener('pointerenter', () => on(false));
        row.addEventListener('pointerleave', () => off(false));
        row.addEventListener('focus', () => on(true));
        row.addEventListener('blur', () => off(true));
      }

      chart.addEventListener('bc:test-chart:highlight', (e) => {
        mark(e.detail ? e.detail.key : null);
      });
    }
  }

  // ---- arm-to-interact release ----------------------------------------------
  //
  // The two ways out that are not a control: Escape, and a click that landed
  // somewhere else. Both are document-level for the same reason bc-rig-figure's
  // are — the reader who has finished with a chart is looking somewhere else by
  // definition, and should not have to come back and find the × first.
  //
  // EVERY CHART BUT THE ONE CLICKED IN. A batch record carries one of these per
  // test configuration, and a click inside chart B is a click OFF chart A; only
  // the host of the click is spared, or the first chart armed would stay armed
  // (holding the wheel over its own box) for the rest of the session.
  const eachChart = (fn) => {
    for (const el of document.querySelectorAll('bc-test-chart')) fn(el);
  };
  const release = (el) => {
    if (typeof el.releaseIfArmed === 'function') el.releaseIfArmed();
  };

  document.addEventListener('click', (e) => {
    const target = e.target;
    // A click on the specimen table beside a chart is not a click AWAY from
    // it: the table and the plot are one figure (see ROW LINKING above), and
    // pressing a row to trace its curve must not put the chart back to sleep.
    // Reading the link container here — rather than in the element — is what
    // keeps the ELEMENT's "never look at the page around you" contract intact:
    // this code IS the linker, and the container is its own scope. How wide
    // that scope is, is the host's call: on the batch page it is the whole
    // configuration section, so a chart stays armed while the reader works
    // inside its own group and lets go when they move to the next one.
    const near = target && target.closest
      ? target.closest('bc-test-chart, [data-bc-chart-link]')
      : null;
    let host = null;
    if (near) host = near.matches('bc-test-chart') ? near : near.querySelector('bc-test-chart');
    eachChart((el) => {
      if (el !== host) release(el);
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') eachChart(release);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => linkRows(document));
  } else {
    linkRows(document);
  }
  // Theme-editor section reloads replace the markup wholesale.
  document.addEventListener('shopify:section:load', (e) => linkRows(e.target || document));
})();
